import { useState, useEffect, useCallback } from 'react';
import { pm } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../theme';

const SPRINT_MANAGE_ROLES = ['project_manager', 'team_lead'];

/* ─── helpers ───────────────────────────────────────────────────── */
const fmt = iso =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';

const fmtInput = iso => iso ? iso.split('T')[0] : '';

const SPRINT_STATUS = {
  draft:     { label:'Draft',     color:'#6B7280', bg:'#F3F4F6' },
  started:   { label:'Active',    color:'#16A34A', bg:'#DCFCE7' },
  completed: { label:'Completed', color:'#2563EB', bg:'#DBEAFE' },
};

const PRIORITY_META = {
  urgent: { label:'Urgent', color:'#EF4444', symbol:'⚡' },
  high:   { label:'High',   color:'#F97316', symbol:'↑' },
  medium: { label:'Medium', color:'#EAB308', symbol:'→' },
  low:    { label:'Low',    color:'#22C55E', symbol:'↓' },
  none:   { label:'None',   color:'#9CA3AF', symbol:'—' },
};

const TYPE_META = {
  task:        { label:'Task',        color:'#3B82F6', icon:'◈' },
  story:       { label:'Story',       color:'#8B5CF6', icon:'◉' },
  bug:         { label:'Bug',         color:'#EF4444', icon:'⬡' },
  feature:     { label:'Feature',     color:'#14B8A6', icon:'★' },
  improvement: { label:'Improvement', color:'#F59E0B', icon:'▲' },
  test:        { label:'Test',        color:'#6B7280', icon:'✓' },
};

const ACCENTS = ['#F97316','#3B82F6','#8B5CF6','#EC4899','#14B8A6','#F59E0B','#22C55E','#EF4444'];
const accentFor = (str = '') => ACCENTS[(str.charCodeAt(0) || 0) % ACCENTS.length];

function Avatar({ name = '', size = 22 }) {
  const bg = accentFor(name);
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:`${bg}20`, color:bg,
                  fontSize: size * 0.42, fontWeight:700, display:'flex',
                  alignItems:'center', justifyContent:'center', flexShrink:0, border:`1.5px solid ${bg}40` }}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

/* ─── SprintFormModal ────────────────────────────────────────────── */
function SprintFormModal({ projectId, sprint, onSave, onClose }) {
  const editing = !!sprint;
  const [form, setForm] = useState({
    name:        sprint?.name        ?? '',
    description: sprint?.description ?? '',
    start_date:  fmtInput(sprint?.start_date),
    end_date:    fmtInput(sprint?.end_date),
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        name:        form.name.trim(),
        description: form.description.trim() || null,
        start_date:  form.start_date || null,
        end_date:    form.end_date   || null,
      };
      if (editing) await pm.updateSprint(sprint.id, payload);
      else         await pm.createSprint(projectId, payload);
      onSave();
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const inputStyle = {
    width:'100%', padding:'0.45rem 0.65rem', border:'1px solid #E4E4E7',
    borderRadius:7, fontSize:'0.82rem', outline:'none', background:'#fff',
    boxSizing:'border-box',
  };
  const labelStyle = { fontSize:'0.72rem', fontWeight:600, color:'#374151', marginBottom:4, display:'block' };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', display:'flex',
                  alignItems:'center', justifyContent:'center', zIndex:2000 }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'#fff', borderRadius:12, width:480, maxWidth:'94vw',
                    boxShadow:'0 8px 40px rgba(0,0,0,0.18)', overflow:'hidden' }}>
        <div style={{ padding:'1rem 1.25rem', borderBottom:'1px solid #F3F4F6',
                      display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ margin:0, fontSize:'0.95rem', fontWeight:700 }}>
            {editing ? 'Edit Sprint' : 'New Sprint'}
          </h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', cursor:'pointer', fontSize:'1.1rem', color:'#9CA3AF' }}>
            ✕
          </button>
        </div>
        <div style={{ padding:'1.25rem', display:'flex', flexDirection:'column', gap:'0.85rem' }}>
          {error && (
            <div style={{ background:'#FEE2E2', color:'#DC2626', padding:'0.5rem 0.75rem',
                          borderRadius:7, fontSize:'0.78rem' }}>{error}</div>
          )}
          <div>
            <label style={labelStyle}>Name *</label>
            <input value={form.name} onChange={set('name')} placeholder="Sprint 1, Q1 Sprint…"
                   style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description} onChange={set('description')}
                      placeholder="What's the goal of this sprint?" rows={3}
                      style={{ ...inputStyle, resize:'vertical', fontFamily:'inherit' }} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
            <div>
              <label style={labelStyle}>Start Date</label>
              <input type="date" value={form.start_date} onChange={set('start_date')} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>End Date</label>
              <input type="date" value={form.end_date} onChange={set('end_date')} style={inputStyle} />
            </div>
          </div>
        </div>
        <div style={{ padding:'0.875rem 1.25rem', borderTop:'1px solid #F3F4F6',
                      display:'flex', justifyContent:'flex-end', gap:'0.5rem' }}>
          <button onClick={onClose}
            style={{ padding:'0.45rem 1rem', border:'1px solid #E4E4E7', borderRadius:7,
                     background:'#fff', cursor:'pointer', fontSize:'0.82rem', fontWeight:600, color:'#374151' }}>
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
            style={{ padding:'0.45rem 1.1rem', border:'none', borderRadius:7,
                     background: saving ? '#9CA3AF' : theme.primary,
                     color:'#fff', cursor: saving ? 'not-allowed' : 'pointer',
                     fontSize:'0.82rem', fontWeight:700 }}>
            {saving ? 'Saving…' : (editing ? 'Update' : 'Create Sprint')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── AddIssuesModal ─────────────────────────────────────────────── */
function AddIssuesModal({ projectId, sprintId, existingIds, onSave, onClose }) {
  const [issues,   setIssues]   = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [search,   setSearch]   = useState('');

  useEffect(() => {
    pm.getIssues(projectId)
      .then(r => setIssues(r.data.filter(i => !existingIds.has(i.id))))
      .finally(() => setLoading(false));
  }, [projectId, existingIds]);

  const toggle = id =>
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const save = async () => {
    if (!selected.length) return;
    setSaving(true);
    await pm.addIssuesToSprint(sprintId, { issue_ids: selected }).catch(() => {});
    setSaving(false);
    onSave();
  };

  const filtered = issues.filter(i =>
    !search || i.title.toLowerCase().includes(search.toLowerCase()) ||
    (i.code && i.code.toLowerCase().includes(search.toLowerCase()))
  );

  const tm = k => TYPE_META[k] || TYPE_META.task;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', display:'flex',
                  alignItems:'center', justifyContent:'center', zIndex:2100 }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'#fff', borderRadius:12, width:540, maxWidth:'94vw', maxHeight:'80vh',
                    boxShadow:'0 8px 40px rgba(0,0,0,0.18)', display:'flex', flexDirection:'column', overflow:'hidden' }}>
        <div style={{ padding:'1rem 1.25rem', borderBottom:'1px solid #F3F4F6',
                      display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ margin:0, fontSize:'0.95rem', fontWeight:700 }}>Add Issues to Sprint</h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', cursor:'pointer', fontSize:'1.1rem', color:'#9CA3AF' }}>
            ✕
          </button>
        </div>
        <div style={{ padding:'0.75rem 1.25rem', borderBottom:'1px solid #F3F4F6' }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
                 placeholder="Search issues…"
                 style={{ width:'100%', padding:'0.45rem 0.65rem', border:'1px solid #E4E4E7',
                          borderRadius:7, fontSize:'0.82rem', outline:'none', boxSizing:'border-box' }} />
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'0.5rem 0' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:'2rem', color:'#9CA3AF', fontSize:'0.82rem' }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:'2rem', color:'#9CA3AF', fontSize:'0.82rem' }}>
              No available issues
            </div>
          ) : filtered.map(issue => {
            const checked = selected.includes(issue.id);
            const t = tm(issue.type);
            return (
              <div key={issue.id} onClick={() => toggle(issue.id)}
                style={{ display:'flex', alignItems:'center', gap:'0.625rem',
                         padding:'0.5rem 1.25rem', cursor:'pointer',
                         background: checked ? '#F5F3FF' : 'transparent',
                         borderLeft: checked ? `3px solid ${theme.primary}` : '3px solid transparent' }}
                onMouseEnter={e => { if (!checked) e.currentTarget.style.background='#F9FAFB'; }}
                onMouseLeave={e => { if (!checked) e.currentTarget.style.background='transparent'; }}>
                <div style={{ width:16, height:16, borderRadius:4, border: checked ? `2px solid ${theme.primary}` : '2px solid #D1D5DB',
                               background: checked ? theme.primary : '#fff', display:'flex', alignItems:'center',
                               justifyContent:'center', flexShrink:0 }}>
                  {checked && <span style={{ color:'#fff', fontSize:'0.6rem', fontWeight:900 }}>✓</span>}
                </div>
                <span style={{ color:t.color, fontSize:'0.72rem', fontWeight:700, flexShrink:0 }}>{t.icon}</span>
                {issue.code && (
                  <span style={{ color:'#14B8A6', fontFamily:'monospace', fontSize:'0.72rem', fontWeight:600, flexShrink:0 }}>
                    {issue.code}
                  </span>
                )}
                <span style={{ fontSize:'0.82rem', color:'#374151', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {issue.title}
                </span>
              </div>
            );
          })}
        </div>
        <div style={{ padding:'0.875rem 1.25rem', borderTop:'1px solid #F3F4F6',
                      display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:'0.78rem', color:'#6B7280' }}>
            {selected.length} selected
          </span>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button onClick={onClose}
              style={{ padding:'0.45rem 1rem', border:'1px solid #E4E4E7', borderRadius:7,
                       background:'#fff', cursor:'pointer', fontSize:'0.82rem', fontWeight:600, color:'#374151' }}>
              Cancel
            </button>
            <button onClick={save} disabled={saving || !selected.length}
              style={{ padding:'0.45rem 1.1rem', border:'none', borderRadius:7,
                       background: (saving || !selected.length) ? '#9CA3AF' : theme.primary,
                       color:'#fff', cursor: (saving || !selected.length) ? 'not-allowed' : 'pointer',
                       fontSize:'0.82rem', fontWeight:700 }}>
              {saving ? 'Adding…' : `Add ${selected.length || ''} Issue${selected.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── SprintDetail ───────────────────────────────────────────────── */
function SprintDetail({ projectId, sprint, onBack, onUpdated, canManage }) {
  const [issues,   setIssues]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showAdd,  setShowAdd]  = useState(false);
  const [removing, setRemoving] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const sm = SPRINT_STATUS[sprint.status] || SPRINT_STATUS.draft;

  const loadIssues = useCallback(() => {
    setLoading(true);
    pm.getSprintIssues(sprint.id)
      .then(r => setIssues(r.data))
      .finally(() => setLoading(false));
  }, [projectId, sprint.id]);

  useEffect(() => { loadIssues(); }, [loadIssues]);

  const removeIssue = async (issueId) => {
    setRemoving(issueId);
    await pm.removeIssueFromSprint(sprint.id, issueId).catch(() => {});
    setRemoving(null);
    loadIssues();
    onUpdated();
  };

  const advanceStatus = async () => {
    const next = sprint.status === 'draft' ? 'started' : sprint.status === 'started' ? 'completed' : null;
    if (!next) return;
    await pm.updateSprint(sprint.id, { status: next }).catch(() => {});
    onUpdated();
  };

  const deleteSprint = async () => {
    if (!window.confirm('Delete this sprint? Issues will not be deleted.')) return;
    setDeleting(true);
    await pm.deleteSprint(sprint.id).catch(() => {});
    onBack();
  };

  const existingIds = new Set(issues.map(i => i.id));
  const completed   = issues.filter(i => ['completed','cancelled'].includes(i.state_group)).length;
  const pct         = issues.length ? Math.round(completed / issues.length * 100) : 0;

  const tm  = k => TYPE_META[k]    || TYPE_META.task;
  const pm2 = k => PRIORITY_META[k] || PRIORITY_META.none;

  return (
    <>
    {editMode && (
      <SprintFormModal projectId={projectId} sprint={sprint}
        onSave={() => { setEditMode(false); onUpdated(); }}
        onClose={() => setEditMode(false)} />
    )}
    {showAdd && (
      <AddIssuesModal projectId={projectId} sprintId={sprint.id} existingIds={existingIds}
        onSave={() => { setShowAdd(false); loadIssues(); onUpdated(); }}
        onClose={() => setShowAdd(false)} />
    )}

    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
      {/* Back header */}
      <div style={{ background:'#fff', borderBottom:'1px solid #E4E4E7',
                    padding:'0.75rem 1.5rem', display:'flex', alignItems:'center',
                    justifyContent:'space-between', flexWrap:'wrap', gap:'0.5rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.625rem' }}>
          <button onClick={onBack}
            style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF',
                     fontSize:'0.78rem', display:'flex', alignItems:'center', gap:4, padding:0 }}
            onMouseEnter={e => e.currentTarget.style.color = theme.primary}
            onMouseLeave={e => e.currentTarget.style.color = '#9CA3AF'}>
            ← Sprints
          </button>
          <span style={{ color:'#D1D5DB' }}>/</span>
          <h2 style={{ margin:0, fontSize:'0.95rem', fontWeight:700, color:'#111827' }}>{sprint.name}</h2>
          <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:sm.bg,
                         color:sm.color, padding:'2px 8px', borderRadius:5, fontSize:'0.67rem', fontWeight:700 }}>
            <span style={{ width:5, height:5, borderRadius:'50%', background:sm.color }} />
            {sm.label}
          </span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          {canManage && sprint.status !== 'completed' && (
            <button onClick={advanceStatus}
              style={{ padding:'0.38rem 0.85rem', border:`1px solid ${theme.primary}`, borderRadius:7,
                       background:'#fff', color:theme.primary, cursor:'pointer',
                       fontSize:'0.78rem', fontWeight:600 }}>
              {sprint.status === 'draft' ? '▶ Start Sprint' : '✓ Complete Sprint'}
            </button>
          )}
          {canManage && (
            <button onClick={() => setEditMode(true)}
              style={{ padding:'0.38rem 0.75rem', border:'1px solid #E4E4E7', borderRadius:7,
                       background:'#fff', cursor:'pointer', fontSize:'0.78rem', fontWeight:600, color:'#374151' }}>
              Edit
            </button>
          )}
          {canManage && (
            <button onClick={deleteSprint} disabled={deleting}
              style={{ padding:'0.38rem 0.75rem', border:'1px solid #FEE2E2', borderRadius:7,
                       background:'#fff', cursor: deleting ? 'not-allowed' : 'pointer',
                       fontSize:'0.78rem', fontWeight:600, color:'#EF4444' }}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
        </div>
      </div>

      <div style={{ flex:1, overflowY:'auto', padding:'1.25rem 1.5rem',
                    display:'flex', flexDirection:'column', gap:'1.25rem' }}>
        {/* Meta + progress */}
        <div style={{ background:'#fff', borderRadius:10, border:'1px solid #E4E4E7',
                      padding:'1rem 1.25rem', display:'flex', gap:'2rem', flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:200 }}>
            {sprint.description && (
              <p style={{ margin:'0 0 0.75rem', fontSize:'0.82rem', color:'#6B7280', lineHeight:1.5 }}>
                {sprint.description}
              </p>
            )}
            <div style={{ display:'flex', gap:'1.5rem', flexWrap:'wrap', fontSize:'0.78rem', color:'#6B7280' }}>
              {sprint.start_date && (
                <div><span style={{ fontWeight:600 }}>Start: </span>{fmt(sprint.start_date)}</div>
              )}
              {sprint.end_date && (
                <div><span style={{ fontWeight:600 }}>End: </span>{fmt(sprint.end_date)}</div>
              )}
              {sprint.owned_by && (
                <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <span style={{ fontWeight:600 }}>Lead: </span>
                  <Avatar name={sprint.owned_by} size={18} />
                  <span>{sprint.owned_by}</span>
                </div>
              )}
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'1.5rem', flexShrink:0 }}>
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:'1.5rem', fontWeight:800, color:'#111827' }}>{issues.length}</div>
              <div style={{ fontSize:'0.72rem', color:'#9CA3AF', fontWeight:600 }}>Issues</div>
            </div>
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:'1.5rem', fontWeight:800, color:'#16A34A' }}>{completed}</div>
              <div style={{ fontSize:'0.72rem', color:'#9CA3AF', fontWeight:600 }}>Done</div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
              <div style={{ fontSize:'1.5rem', fontWeight:800, color: pct === 100 ? '#16A34A' : '#2563EB' }}>
                {pct}%
              </div>
              <div style={{ width:120, height:6, background:'#E4E4E7', borderRadius:9999, overflow:'hidden' }}>
                <div style={{ width:`${pct}%`, height:'100%',
                              background: pct === 100 ? '#16A34A' : theme.primary,
                              borderRadius:9999, transition:'width 0.4s' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Issues */}
        <div style={{ background:'#fff', borderRadius:10, border:'1px solid #E4E4E7', overflow:'hidden' }}>
          <div style={{ padding:'0.75rem 1rem', borderBottom:'1px solid #F3F4F6',
                        display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:'0.82rem', fontWeight:700, color:'#374151' }}>
              Issues ({issues.length})
            </span>
            <button onClick={() => setShowAdd(true)}
              style={{ padding:'0.35rem 0.85rem', border:'none', borderRadius:6,
                       background:theme.primary, color:'#fff', cursor:'pointer',
                       fontSize:'0.78rem', fontWeight:700 }}>
              + Add Issues
            </button>
          </div>
          {loading ? (
            <div style={{ padding:'2.5rem', textAlign:'center', color:'#9CA3AF', fontSize:'0.82rem' }}>
              Loading issues…
            </div>
          ) : issues.length === 0 ? (
            <div style={{ padding:'3rem', textAlign:'center' }}>
              <div style={{ fontSize:'2rem', marginBottom:'0.5rem' }}>◉</div>
              <p style={{ color:'#6B7280', fontSize:'0.85rem', margin:0 }}>No issues in this sprint yet.</p>
              <button onClick={() => setShowAdd(true)}
                style={{ marginTop:'0.75rem', padding:'0.45rem 1rem', border:'none', borderRadius:7,
                         background:theme.primary, color:'#fff', cursor:'pointer',
                         fontSize:'0.78rem', fontWeight:700 }}>
                Add Issues
              </button>
            </div>
          ) : issues.map((issue, idx) => {
            const t  = tm(issue.type);
            const p2 = pm2(issue.priority);
            const isDone = ['completed','cancelled'].includes(issue.state_group);
            return (
              <div key={issue.id}
                style={{ display:'flex', alignItems:'center', gap:'0.625rem', padding:'0.6rem 1rem',
                         borderBottom: idx < issues.length - 1 ? '1px solid #F9FAFB' : 'none',
                         background:'#fff' }}
                onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
                onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                <span title={t.label} style={{ color:t.color, fontSize:'0.8rem', flexShrink:0 }}>{t.icon}</span>
                {issue.code && (
                  <span style={{ color:'#14B8A6', fontFamily:'monospace', fontSize:'0.72rem',
                                 fontWeight:600, flexShrink:0 }}>{issue.code}</span>
                )}
                <span style={{ flex:1, fontSize:'0.82rem', color: isDone ? '#9CA3AF' : '#374151',
                               textDecoration: isDone ? 'line-through' : 'none',
                               overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {issue.title}
                </span>
                {issue.state_name && (
                  <span style={{ fontSize:'0.67rem', fontWeight:600, padding:'2px 7px', borderRadius:5,
                                 background:'#F3F4F6', color:'#6B7280', flexShrink:0 }}>
                    {issue.state_name}
                  </span>
                )}
                <span title={p2.label} style={{ color:p2.color, fontSize:'0.78rem', fontWeight:700, flexShrink:0 }}>
                  {p2.symbol}
                </span>
                {issue.assignees?.length > 0 && (
                  <div style={{ display:'flex', gap:2, flexShrink:0 }}>
                    {issue.assignees.slice(0,3).map(a => (
                      <Avatar key={a.id} name={a.name} size={20} />
                    ))}
                  </div>
                )}
                <button onClick={() => removeIssue(issue.id)} disabled={removing === issue.id}
                  title="Remove from sprint"
                  style={{ background:'none', border:'none', cursor: removing === issue.id ? 'not-allowed' : 'pointer',
                           color:'#9CA3AF', fontSize:'0.85rem', padding:'0 4px', flexShrink:0,
                           lineHeight:1, borderRadius:4 }}
                  onMouseEnter={e => e.currentTarget.style.color='#EF4444'}
                  onMouseLeave={e => e.currentTarget.style.color='#9CA3AF'}>
                  {removing === issue.id ? '…' : '✕'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
    </>
  );
}

/* ─── SprintCard ─────────────────────────────────────────────────── */
function SprintCard({ sprint, onOpen, onEdit, onDelete, canManage }) {
  const sm  = SPRINT_STATUS[sprint.status] || SPRINT_STATUS.draft;
  const pct = sprint.completion_pct ?? 0;

  const today     = new Date(); today.setHours(0,0,0,0);
  const endDate   = sprint.end_date ? new Date(sprint.end_date) : null;
  const isOverdue = endDate && endDate < today && sprint.status !== 'completed';

  return (
    <div style={{ background:'#fff', borderRadius:10, border:'1px solid #E4E4E7',
                  overflow:'hidden', transition:'box-shadow 0.12s' }}
      onMouseEnter={e => e.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,0.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
      <div style={{ padding:'1rem 1.125rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.5rem' }}>
          <div style={{ flex:1, minWidth:0 }}>
            <button onClick={onOpen}
              style={{ background:'none', border:'none', cursor:'pointer', padding:0, textAlign:'left',
                       fontSize:'0.9rem', fontWeight:700, color:'#111827', display:'block',
                       overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'100%' }}
              onMouseEnter={e => e.currentTarget.style.color = theme.primary}
              onMouseLeave={e => e.currentTarget.style.color = '#111827'}>
              🔄 {sprint.name}
            </button>
            {sprint.description && (
              <p style={{ margin:'0.2rem 0 0', fontSize:'0.75rem', color:'#9CA3AF',
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {sprint.description}
              </p>
            )}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', flexShrink:0, marginLeft:'0.75rem' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:3, background:sm.bg,
                           color:sm.color, padding:'2px 7px', borderRadius:5, fontSize:'0.65rem', fontWeight:700 }}>
              <span style={{ width:5, height:5, borderRadius:'50%', background:sm.color }} />
              {sm.label}
            </span>
            {canManage && (
              <button onClick={onEdit}
                style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF',
                         fontSize:'0.78rem', padding:'2px 6px', borderRadius:4 }}
                title="Edit"
                onMouseEnter={e => e.currentTarget.style.color='#374151'}
                onMouseLeave={e => e.currentTarget.style.color='#9CA3AF'}>
                ✏
              </button>
            )}
            {canManage && (
              <button onClick={onDelete}
                style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF',
                         fontSize:'0.8rem', padding:'2px 6px', borderRadius:4 }}
                title="Delete"
                onMouseEnter={e => e.currentTarget.style.color='#EF4444'}
                onMouseLeave={e => e.currentTarget.style.color='#9CA3AF'}>
                🗑
              </button>
            )}
          </div>
        </div>

        {/* Dates */}
        <div style={{ display:'flex', alignItems:'center', gap:'1rem', fontSize:'0.73rem',
                      color: isOverdue ? '#EF4444' : '#9CA3AF', marginBottom:'0.75rem' }}>
          {sprint.start_date && <span>📅 {fmt(sprint.start_date)}</span>}
          {sprint.start_date && sprint.end_date && <span style={{ color:'#D1D5DB' }}>→</span>}
          {sprint.end_date && (
            <span style={{ color: isOverdue ? '#EF4444' : '#9CA3AF', fontWeight: isOverdue ? 700 : 400 }}>
              {fmt(sprint.end_date)}{isOverdue ? ' · Overdue' : ''}
            </span>
          )}
        </div>

        {/* Progress */}
        <div style={{ display:'flex', alignItems:'center', gap:'0.625rem', marginBottom:'0.5rem' }}>
          <div style={{ flex:1, height:5, background:'#E4E4E7', borderRadius:9999, overflow:'hidden' }}>
            <div style={{ width:`${pct}%`, height:'100%',
                          background: pct === 100 ? '#16A34A' : theme.primary,
                          borderRadius:9999, transition:'width 0.4s' }} />
          </div>
          <span style={{ fontSize:'0.7rem', fontWeight:700,
                         color: pct === 100 ? '#16A34A' : theme.primary, minWidth:30, textAlign:'right' }}>
            {pct}%
          </span>
        </div>

        {/* Counts */}
        <div style={{ display:'flex', gap:'0.5rem' }}>
          {[
            { v: sprint.total_issues     ?? 0, label:'Issues', color:'#2563EB', bg:'#DBEAFE' },
            { v: sprint.completed_issues ?? 0, label:'Done',   color:'#16A34A', bg:'#DCFCE7' },
          ].map(s => (
            <div key={s.label}
              style={{ background:s.bg, color:s.color, borderRadius:5, padding:'1px 7px',
                       fontSize:'0.67rem', fontWeight:700, display:'flex', alignItems:'center', gap:3 }}>
              {s.v} <span style={{ fontWeight:500, opacity:0.75 }}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Sprints (main) ─────────────────────────────────────────────── */
export default function Sprints({ project }) {
  const { user } = useAuth();
  const [sprints,     setSprints]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showCreate,  setShowCreate]  = useState(false);
  const [editTarget,  setEditTarget]  = useState(null);
  const [openSprint,  setOpenSprint]  = useState(null);

  const projectId = project.id;
  const canManageSprint = SPRINT_MANAGE_ROLES.includes(user?.role) ||
    (user?.emp_id != null && project?.manager_id != null &&
     Number(user.emp_id) === Number(project.manager_id));

  const load = useCallback(() => {
    setLoading(true);
    pm.getSprints(projectId)
      .then(r => setSprints(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (sprint) => {
    if (!window.confirm(`Delete sprint "${sprint.name}"? Issues will not be deleted.`)) return;
    await pm.deleteSprint(sprint.id).catch(() => {});
    load();
  };

  /* ── If a sprint is open, show detail view ── */
  if (openSprint) {
    const fresh = sprints.find(s => s.id === openSprint.id) || openSprint;
    return (
      <SprintDetail
        projectId={projectId}
        sprint={fresh}
        onBack={() => { setOpenSprint(null); load(); }}
        onUpdated={load}
        canManage={canManageSprint}
      />
    );
  }

  /* ── Grouped: active / upcoming / completed ── */
  const active    = sprints.filter(s => s.status === 'started');
  const draft     = sprints.filter(s => s.status === 'draft');
  const completed = sprints.filter(s => s.status === 'completed');

  const Section = ({ title, icon, items, emptyMsg }) => (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem',
                    marginBottom:'0.75rem', padding:'0 0.25rem' }}>
        <span style={{ fontSize:'0.75rem' }}>{icon}</span>
        <span style={{ fontSize:'0.82rem', fontWeight:700, color:'#374151' }}>{title}</span>
        <span style={{ fontSize:'0.72rem', color:'#9CA3AF', fontWeight:500 }}>({items.length})</span>
      </div>
      {items.length === 0 ? (
        <div style={{ padding:'1rem 1.25rem', background:'#F9FAFB', borderRadius:8,
                      fontSize:'0.78rem', color:'#9CA3AF', textAlign:'center' }}>
          {emptyMsg}
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:'0.75rem' }}>
          {items.map(s => (
            <SprintCard key={s.id} sprint={s}
              onOpen={() => setOpenSprint(s)}
              onEdit={() => setEditTarget(s)}
              onDelete={() => handleDelete(s)}
              canManage={canManageSprint} />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
    {(showCreate || editTarget) && (
      <SprintFormModal
        projectId={projectId}
        sprint={editTarget}
        onSave={() => { setShowCreate(false); setEditTarget(null); load(); }}
        onClose={() => { setShowCreate(false); setEditTarget(null); }}
      />
    )}

    <div style={{ padding:'1.25rem 1.5rem', display:'flex', flexDirection:'column', gap:'1.5rem' }}>
      {/* Toolbar */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <h2 style={{ margin:0, fontSize:'1rem', fontWeight:700, color:'#111827' }}>Sprints</h2>
          <p style={{ margin:'0.15rem 0 0', fontSize:'0.78rem', color:'#9CA3AF' }}>
            Time-boxed iterations to plan and track work
          </p>
        </div>
        {canManageSprint && (
          <button onClick={() => setShowCreate(true)}
            style={{ padding:'0.45rem 1.1rem', border:'none', borderRadius:8,
                     background:theme.primary, color:'#fff', cursor:'pointer',
                     fontSize:'0.82rem', fontWeight:700, display:'flex', alignItems:'center', gap:'0.35rem' }}>
            + New Sprint
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
                      justifyContent:'center', gap:'0.75rem', padding:'5rem 0', color:'#9CA3AF' }}>
          <div style={{ width:32, height:32, border:'3px solid #E4E4E7',
                        borderTopColor:theme.primary, borderRadius:'50%',
                        animation:'spin 0.8s linear infinite' }} />
          <span style={{ fontSize:'0.82rem' }}>Loading sprints…</span>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      ) : sprints.length === 0 ? (
        <div style={{ textAlign:'center', padding:'5rem 0' }}>
          <div style={{ fontSize:'3rem', marginBottom:'0.75rem', opacity:0.3 }}>🔄</div>
          <p style={{ fontWeight:700, color:'#111827', margin:'0 0 0.25rem' }}>No sprints yet</p>
          <p style={{ color:'#9CA3AF', fontSize:'0.82rem', margin:'0 0 1.25rem' }}>
            Create time-boxed iterations to organise and track your team's work.
          </p>
          {canManageSprint && (
            <button onClick={() => setShowCreate(true)}
              style={{ padding:'0.5rem 1.25rem', border:'none', borderRadius:8,
                       background:theme.primary, color:'#fff', cursor:'pointer',
                       fontSize:'0.85rem', fontWeight:700 }}>
              Create First Sprint
            </button>
          )}
        </div>
      ) : (
        <>
          <Section title="Active" icon="▶" items={active}
            emptyMsg="No active sprints — start a draft sprint to begin tracking." />
          <Section title="Draft / Upcoming" icon="○" items={draft}
            emptyMsg="No upcoming sprints." />
          <Section title="Completed" icon="✓" items={completed}
            emptyMsg="No completed sprints yet." />
        </>
      )}
    </div>
    </>
  );
}
